import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete, Visibility } from "@mui/icons-material";
import CustomButton from "../../Components/CustomButton";
import { useNavigate, useLocation } from "react-router";
import { NEWPURCHASE } from "../../Routes/constURL";
import productApi from "../../service/productApi";
import ConfirmDeleteModal from "../../Components/shared/Modals/ConfirmDeleteModal";
import CustomSnackbar from "../../Components/CustomSnackbar";
import { toast } from "react-toastify";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const SimplePurchase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    if (location.state?.successMessage) {
      toast.success(location.state.successMessage);
      // Clear the state so the toast doesn't re-appear on re-renders
      navigate(location.pathname, { replace: true });
    }
    fetchPurchaseOrder();
    // location.state is a dependency to show toast when navigating back with a message
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  //Fetch data
  const fetchPurchaseOrder = async () => {
    setLoading(true);
    try {
      const response = await productApi.getPurchaseOrder();
      if (response && response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        toast.error("Something went wrong.");
        setData([]);
      }
    } catch (error) {
      console.error("Failed to fetch purchase order:", error);
      toast.error("Failed to fetch purchase orders.");
    } finally {
      setLoading(false);
    }
  };

  // Delete item
  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setItemToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await productApi.deletePurchaseOrder(itemToDelete);
      fetchPurchaseOrder();
    } catch (error) {
      console.error("Failed to delete purchase order:", error);
      toast.error("Failed to delete purchase order.");
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleViewDetails = (id) => {
    const orderDetails = data.filter(item => item.id === id);
    navigate(NEWPURCHASE, {state: {orderDetails, mode: "VIEW"}});
  };

  // Edit item
  const handleEdit = (id) => {
    const orderDetails = data.filter(item => item.id === id);
    navigate(NEWPURCHASE, {state: { orderDetails, mode: "EDIT" }});
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
      <CustomSnackbar />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          paddingY: { xs: 1, md: 2 },
          paddingX: { xs: 1.5, md: 3 },
          borderRadius: 2,
          mt: 1,
          flexWrap: "wrap",
          rowGap: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, textWrap: "nowrap" }}
        >
          Simple Purchase
        </Typography>
        <CustomButton
          text="New Purchase"
          bgcolor="#95ABEB"
          color="black"
          sx={{
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            padding: { xs: "4px 10px", md: "6px 16px" },
          }}
          onClick={() => navigate(NEWPURCHASE, {state: {mode: "ADD"}})}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              border: "1px solid #e0e0e0",
              "& thead": {
                backgroundColor: "#f9fafb",
                "& th": {
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  textWrap: "nowrap",
                  color: "#333",
                },
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow className="bg-gray-100">
                  <TableCell className="font-semibold">Supplier</TableCell>
                  <TableCell className="font-semibold">Order Number</TableCell>
                  <TableCell className="font-semibold">Amount</TableCell>
                  <TableCell className="font-semibold">Currency</TableCell>
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell
                    sx={{ textAlign: "end" }}
                    className="font-semibold"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography>No data found</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.id} className="hover:bg-gray-50">
                      <TableCell>{row.organisations[0].name}</TableCell>
                      <TableCell>{row.order_number}</TableCell>
                      <TableCell>{row.total_amount}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell sx={{ textAlign: "end" }}>
                        <IconButton
                          onClick={() => handleViewDetails(row.id)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          onClick={() => handleEdit(row.id)}
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(row.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Delete sx={{ color: "#c40c0c" }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        handleDelete={handleConfirmDelete}
      />
    </Box>
  );
};

export default SimplePurchase;
